import { z } from 'zod';
import { ME_TOKEN } from './filter';
import type { FilterCondition, FilterFieldType } from './filter';
import type { FlowView } from './view';
// Type-only: erased at compile time, so this creates NO runtime import edge
// back to filter-fields.ts. filter-fields.ts (the entity-catalog SINK) is
// the one that imports real values from THIS file (SIGNAL_FIELDS,
// SIGNAL_DISPLAY_FILTER_FIELDS, SIGNAL_SYNTHETIC_COLUMN_KEYS) to build the
// 'signal' arm of `filterCatalogByKey`/`visibleColumnKeys` and to compose
// `SIGNAL_FILTER_FIELDS` (which needs `TERRA_SIGNALS_FILTER_FIELDS`, native
// to filter-fields.ts). A real (non-type) import in either direction would
// make the two modules circular — Bun/ESM resolves that by handing the
// second-loaded side a partially-initialized namespace, which throws
// "Cannot access '<x>' before initialization" the moment either module's
// OWN top-level code (not a function body) reads a not-yet-defined export
// from the other, and WHICH module fails depends on which one a given
// caller happens to import first — verified empirically against this
// exact shape before writing this file. `SIGNAL_FIELDS`/`CONTACT_FIELDS`/
// `ACCOUNT_FIELDS` are all leaves for the same reason contact-view.ts and
// account-view.ts never import filter-fields.ts today; this file follows
// that existing precedent rather than inventing a new one.
import type { FilterFieldCatalogEntry } from './filter-fields';
import { IsoDateStringSchema, TerraSignalRowSchema } from '../schemas/terra-signals';
import { CONTACT_FIELDS } from './contact-view';
import { ACCOUNT_FIELDS } from './account-view';
import { GongProbeStatusSchema } from './signal-verification';
import { REP_STATES, type RepState } from './rep-state';

/**
 * Signals tab view contract (plan 2026-09-24-002 U2; UX map
 * 2026-09-24-001 sections 4/4a, which ARE the spec for columns/filters/
 * presets — no invented columns or filters here).
 *
 * `SIGNAL_FIELDS` is the COLUMN catalog (labels for the table header +
 * Columns popover), independent of `SIGNAL_FILTER_FIELDS` (the FILTER
 * catalog, defined in filter-fields.ts per the doc comment above —
 * DECOUPLED from columns exactly like CONTACT_FIELDS/CONTACT_FILTER_FIELDS
 * already are).
 */

// ── Contact / Account passthrough (Signals V3 PR 1, U3) ─────────────────────
//
// The Signals list already joins `salesforce_contact sc` and
// `salesforce_account acc` but offered ~20 of their fields, against ~100 on
// People / Companies. Every plain column those two tabs offer is now offered
// here too, GENERATED from `CONTACT_FIELDS` / `ACCOUNT_FIELDS` (the catalogs
// People/Companies use) so the three tabs can't drift. The server twin
// (`src/api/flow/signal-passthrough-fields.ts`) builds the SQL entries from
// THIS list against the real `CONTACT_FIELD_MAP` / `ACCOUNT_FIELD_MAP`, and
// throws at module load if a listed field is not a plain column there — so the
// exclusion rules below are checked, not trusted. No new join: both tables
// are already joined.

export type SignalPassthroughEntity = 'contact' | 'account';

export interface SignalPassthroughField {
  /** Signals key: `contact_<key>` / `account_<key>` (never doubled when the source key already carries the prefix). */
  key: string;
  entity: SignalPassthroughEntity;
  /** The key in `CONTACT_FIELDS`/`CONTACT_FIELD_MAP` (or the account twins) this field is generated from. */
  sourceKey: string;
  label: string;
  type: FilterFieldType;
  values?: readonly string[];
}

/**
 * Source fields that are NOT plain columns of the joined row, or are offered
 * elsewhere. Contact side: the contact's own owner/ClayDR/GS (the rep means
 * the ACCOUNT's — U4 — and PR 1 deliberately has no contact-level copy),
 * derived expressions, and keys that already exist on Signals under the same
 * final name (`name`, `title`, … — the hand-written fields above and the
 * `terra_signals` view's own `contact_name`/`account_name`).
 */
const CONTACT_PASSTHROUGH_EXCLUDED_KEYS: ReadonlySet<string> = new Set([
  'name', 'title', 'persona', 'contact_stage', 'mailing_city', 'mailing_state', 'mailing_country',
  'account_name', 'has_workspace', 'owner_name', 'claydr_name', 'gs_name',
  'past_product_user', 'came_from', 'has_responded', 'last_queue_send_date',
]);

/** Contact source keys carrying these prefixes are another entity's data (account_*), Terra-only state (tracker_*), EXISTS/lateral signal columns (signal_*), play-membership columns (play_*), or successor-contact joins (new_*). */
const CONTACT_PASSTHROUGH_EXCLUDED_PREFIXES: readonly string[] = ['account_', 'tracker_', 'signal_', 'play_', 'new_'];

/** Account side: person fields (U4 owns them as `account_owner_name`/`account_claydr_name`/`account_gs_name`), keys already present, and columns from other tables (`act.`/`cu.`/`t.`). */
const ACCOUNT_PASSTHROUGH_EXCLUDED_KEYS: ReadonlySet<string> = new Set([
  'name', 'owner_name', 'claydr_name', 'gs_name', 'industry', 'sales_tier', 'product_tier', 'account_fit',
  'billing_country', 'email_domain_link', 'contract_end', 'credit_pace', 'credits_l30', 'tracker_date', 'tracker_note',
]);

function passthroughKey(entity: SignalPassthroughEntity, sourceKey: string): string {
  return sourceKey.startsWith(`${entity}_`) ? sourceKey : `${entity}_${sourceKey}`;
}

/** Signals-only label overrides for generated passthrough keys (the People tab keeps its own names). */
export const SIGNAL_PASSTHROUGH_LABEL_OVERRIDES: ReadonlyMap<string, string> = new Map([
  ['account_fit_score', 'Account fit score'],
  ['contact_gong_active_flows', 'Contact active flows'],
  ['contact_gong_in_flow', 'Contact in a flow'],
  ['contact_gong_flow_name', 'Contact flow name'],
]);

function buildSignalPassthroughFields(): SignalPassthroughField[] {
  const out: SignalPassthroughField[] = [];
  for (const f of CONTACT_FIELDS) {
    if (f.noFilter || CONTACT_PASSTHROUGH_EXCLUDED_KEYS.has(f.key)) continue;
    if (CONTACT_PASSTHROUGH_EXCLUDED_PREFIXES.some(prefix => f.key.startsWith(prefix))) continue;
    out.push({ key: passthroughKey('contact', f.key), entity: 'contact', sourceKey: f.key,
      label: SIGNAL_PASSTHROUGH_LABEL_OVERRIDES.get(passthroughKey('contact', f.key)) ?? f.label, type: f.type });
  }
  for (const f of ACCOUNT_FIELDS) {
    if (f.noFilter || ACCOUNT_PASSTHROUGH_EXCLUDED_KEYS.has(f.key)) continue;
    out.push({
      key: passthroughKey('account', f.key),
      entity: 'account',
      sourceKey: f.key,
      // The Account fields sit beside the contact's own in the Columns list; a bare "ARR" would read as the contact's.
      label: SIGNAL_PASSTHROUGH_LABEL_OVERRIDES.get(passthroughKey('account', f.key))
        ?? (f.label.startsWith('Account') ? f.label : `Account ${f.label}`),
      type: f.type,
      ...(f.values ? { values: f.values } : {}),
    });
  }
  return out;
}

export const SIGNAL_PASSTHROUGH_FIELDS: readonly SignalPassthroughField[] = buildSignalPassthroughFields();

/**
 * Passthrough date columns Postgres stores as a calendar DATE (no clock):
 * relative-HOUR filters are meaningless on them (U6 `granularity: 'day'`) and
 * they render with `fmtDateOnly`. Hand-listed here because this module is
 * shared/browser-safe and cannot read `PG_DATE_COLUMN_TYPES` (services); the
 * server contract test asserts this set equals the real DATE columns.
 */
export const SIGNAL_PASSTHROUGH_DAY_KEYS: ReadonlySet<string> = new Set([
  'contact_last_activity', 'contact_job_start_date', 'contact_last_trial_start',
  'account_renewal_date', 'account_next_gs_assign_date',
]);

// ── Column catalog ───────────────────────────────────────────────────────────

export interface SignalFieldDef {
  key: string;
  label: string;
  type: FilterFieldType;
  /** Not offered in the filter builder — client-computed or link-only. */
  noFilter?: boolean;
}

/**
 * Every column in UX map 4a, in the table's own row order. `local_time` is
 * client-computed (attached to each row in the browser after fetch, same as
 * Action View) and `source_link` is a computed link — both `noFilter: true`.
 * The Ranker `priority` NUMBER is no longer a column (Signals V3 PR 1, U5 —
 * reps read order, not a score); the ranking still orders rows through the
 * sort key `priority` (`SIGNAL_SYNTHETIC_COLUMN_KEYS`), and a saved view that
 * persisted the column is healed by `migrateLegacyViewFieldKeys`. `contact_location` is a FOURTH client-computed
 * column (joined in the browser from the three `contact_mailing_*` fields
 * the row already carries, the Accounts `hq` pattern) that is also
 * `noFilter: true` but is NOT in `SIGNAL_SYNTHETIC_COLUMN_KEYS` below — it is
 * a real catalog column, accepted by `visibleColumnKeys('signal')`, that
 * persists in saved views and is simply never a sort/group/filter key. The
 * rest are either `terra_signals` view columns or contact/account display
 * joins attached at list-route time (U3's job, not this one) but are
 * filterable/sortable/columnable here regardless of join mechanics, matching
 * `SignalRowSchema` below.
 */
export const SIGNAL_FIELDS: SignalFieldDef[] = [
  { key: 'contact_name',              label: 'Contact',       type: 'text' },
  { key: 'contact_linkedin',          label: 'LinkedIn',      type: 'text', noFilter: true },
  { key: 'contact_title',             label: 'Title',         type: 'text' },
  { key: 'contact_persona',           label: 'Persona',       type: 'picklist' },
  { key: 'contact_stage',             label: 'Contact stage', type: 'picklist' },
  { key: 'contact_mailing_city',      label: 'City',          type: 'text' },
  { key: 'contact_location',          label: 'Location',      type: 'text', noFilter: true },
  { key: 'local_time',                label: 'Local time',    type: 'text', noFilter: true },
  { key: 'step_since',                label: 'Days on step',  type: 'text', noFilter: true },
  { key: 'account_name',              label: 'Account',       type: 'text' },
  { key: 'account_email_domain_link', label: 'Domain',        type: 'text' },
  { key: 'account_linkedin',          label: 'Account LinkedIn', type: 'text', noFilter: true },
  { key: 'account_sales_tier',        label: 'Tier',          type: 'picklist' },
  { key: 'account_industry',          label: 'Industry',      type: 'picklist' },
  { key: 'account_fit',               label: 'Account fit',   type: 'picklist' },
  { key: 'account_product_tier',      label: 'Product tier',  type: 'picklist' },
  { key: 'account_billing_country',   label: 'Account country', type: 'text' },
  { key: 'signal_source_type',        label: 'Signal type',    type: 'picklist' },
  { key: 'signal_source_subtype',     label: 'Signal subtype', type: 'picklist' },
  { key: 'signal_source',             label: 'Signal source', type: 'picklist' },
  { key: 'campaign_name',             label: 'Campaign', type: 'text' },
  { key: 'flow_name',                 label: 'Flow',     type: 'text' },
  { key: 'signal_tactic',             label: 'Step type', type: 'picklist' },
  { key: 'step_number',               label: 'Flow step number', type: 'number' },
  { key: 'signal_status',             label: 'Gong flow status', type: 'picklist' },
  { key: 'flow_enrolled_at',          label: 'Enrolled in flow', type: 'date' },
  { key: 'step_date',                 label: 'Last step completed', type: 'date' },
  { key: 'due_date',                  label: 'Task due date', type: 'date' },
  { key: 'event_date',                label: 'Signal date', type: 'date' },
  { key: 'days_open',                 label: 'Days open',     type: 'number' },
  { key: 'assignee',                  label: 'Assignee',      type: 'person' },
  { key: 'assignee_role',             label: 'Assignee role', type: 'text' },
  { key: 'action_state',              label: 'Action status', type: 'picklist' },
  { key: 'snooze_until',              label: 'Snoozed until', type: 'date' },
  { key: 'action_reason',             label: 'Archive reason', type: 'text' },
  { key: 'action_completed_at',       label: 'Closed date',   type: 'date' },
  { key: 'action_completed_by_name',  label: 'Closed by',     type: 'person' },
  { key: 'source_link',               label: 'Link to source', type: 'text', noFilter: true },
  // Gong reconcile U24 — wire-only on the row (U23); a display column for the Fix in Gong view.
  { key: 'gong_owner_email',          label: 'Gong owner',    type: 'text', noFilter: true },
  // Signals V3 PR 1, U4 — who owns the ACCOUNT (the rep means this, not the
  // contact's own assignment), then U3's generated Contact/Account columns.
  { key: 'account_owner_name',        label: 'Account owner',     type: 'person' },
  { key: 'account_claydr_name',       label: 'Account ClayDR',    type: 'person' },
  { key: 'account_gs_name',           label: 'Growth Strategist', type: 'person' },
  ...SIGNAL_PASSTHROUGH_FIELDS.map(({ key, label, type }): SignalFieldDef => ({ key, label, type })),
];

/** Columns with no filter of their own, mapped to the filter fields a rep should be pointed at instead.
 *  `label` is read from SIGNAL_FIELDS by key. */
const SIGNAL_COLUMN_FILTER_ALIAS_DEFS: ReadonlyArray<{ columnKey: string; targets: readonly string[] }> = [
  { columnKey: 'local_time', targets: ['business_hours'] },
  // step_since is COALESCE(last_step_completed_date, created_date::date): step_date alone can't select never-completed rows.
  { columnKey: 'step_since', targets: ['step_date', 'flow_enrolled_at'] },
  { columnKey: 'contact_location', targets: ['contact_mailing_city', 'contact_mailing_state', 'contact_mailing_country'] },
];
export const SIGNAL_COLUMN_FILTER_ALIASES: ReadonlyArray<{ columnKey: string; label: string; targets: readonly string[] }> =
  SIGNAL_COLUMN_FILTER_ALIAS_DEFS.map(a => ({
    ...a,
    label: SIGNAL_FIELDS.find(f => f.key === a.columnKey)?.label ?? a.columnKey,
  }));

/** The client-computed columns (Ranker `priority`, business-hours
 *  `local_time`, day-level `step_since` derived from `step_started_at` —
 *  Signals V3 PR 2, U4) — the only sort/column keys that resolve to neither
 *  the filter catalog NOR a `terra_signals`/join column. Read by
 *  `filter-fields.ts`'s `validateViewFieldReferences` (sort), by the list
 *  route's `rejectSyntheticSortOrGroup` (the server cannot sort/group on
 *  them) and by the client's attach step in `useSignalTableModel`. */
export const SIGNAL_SYNTHETIC_COLUMN_KEYS: string[] = ['priority', 'local_time', 'step_since'];

/** The client-sortable subset of `SIGNAL_SYNTHETIC_COLUMN_KEYS`: the browser re-sorts the
 *  loaded page by these. `step_since` is column-only — nothing sorts by it, and the list
 *  route 400s a sort naming it — so a saved view must not be able to name it as its sort. */
export const SIGNAL_SYNTHETIC_SORT_KEYS: readonly string[] = ['priority', 'local_time'];

/**
 * QA batch 3, D-A step 1 — group-only client-computed keys: `local_time`
 * (also a `SIGNAL_SYNTHETIC_COLUMN_KEYS` column, since it doubles as a sort
 * key) and `business_hours` (a group-only key with no column at all — D-D).
 * Neither the server nor `validateViewFieldReferences`'s catalog lookup can
 * resolve either key; both are legitimate `group_by` levels for `'signal'`
 * arranged entirely in the browser (`arrangeSignalsClientSide`, G3).
 */
export const SIGNAL_SYNTHETIC_GROUP_KEYS: string[] = ['local_time', 'business_hours'];

/** Column keys the Signals catalog renamed after a view could persist them. QA batch 3 G1 §1.3 moved the
 *  Subtype COLUMN from the view's play-scope `signal_subtype` to the raw SFDC `signal_source_subtype`;
 *  `signal_subtype` stays a valid FILTER field ('Signal Subtype', TERRA_SIGNALS_FILTER_FIELDS) and is never
 *  rewritten inside `filter`. Applied by `migrateLegacyViewFieldKeys` (filter-fields.ts) on every read and
 *  write of a Signals definition or working state (fix-plan-batch3.md D-B). */
/** Column keys that were removed outright (not renamed) — dropped from a saved view's `columns` on read/write
 *  (`migrateLegacyViewFieldKeys`). `priority` (the visible Ranker number, Signals V3 PR 1 U5): the ranking still
 *  orders rows through the `priority` SORT key, so only the column goes. */
export const SIGNAL_DROPPED_COLUMN_KEYS: readonly string[] = ['priority'];

export const SIGNAL_LEGACY_COLUMN_KEYS: Readonly<Record<string, string>> = { signal_subtype: 'signal_source_subtype' };

// ── Display-join filter fields (Contact / Account / Gong Flow) ─────────────

/**
 * Filterable fields that exist only via a display-time join (contact/
 * account mirror row, or the Gong Flow join U3 adds) — NOT already covered
 * by `TERRA_SIGNALS_FILTER_FIELDS` (the `terra_signals` view's own columns).
 * Spread with that catalog into `SIGNAL_FILTER_FIELDS` (filter-fields.ts,
 * which natively holds `TERRA_SIGNALS_FILTER_FIELDS` — see this file's
 * header comment on why the composition lives there, not here).
 */
/** Rep-facing label per `RepState` (the `gong_state` filter picklist). */
const REP_STATE_LABELS: Readonly<Record<RepState, string>> = {
  do_it_now: 'Do it now',
  waiting: 'Waiting',
  fix_in_gong: 'Fix in Gong',
  done: 'Done',
};

// A key that is also a `SIGNAL_FIELDS` column takes the COLUMN's label (`unifyLabels`
// overwrites a hand-written one), so those entries spell the column label here.
const HAND_WRITTEN_DISPLAY_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  { key: 'contact_title',            label: 'Title',           group: 'Contact',   type: 'text' },
  { key: 'contact_mailing_city',     label: 'City',            group: 'Contact',   type: 'text' },
  { key: 'contact_mailing_state',    label: 'State',   group: 'Contact',   type: 'text' },
  { key: 'contact_mailing_country',  label: 'Country', group: 'Contact',   type: 'text' },
  // The two URL fields exist in the filter catalog because
  // flow-signals-list-contract.test.ts requires every non-`ts.*`
  // SIGNAL_LIST_FIELD_MAP key to be here; `allowedOperators` narrows them to
  // the only operators meaningful on a URL ("has a LinkedIn" / "doesn't").
  { key: 'contact_linkedin',         label: 'LinkedIn',        group: 'Contact',   type: 'text', allowedOperators: ['is_empty', 'is_not_empty'] },
  // QA batch 3, D-E — plain SFDC display-join columns Action View already
  // groups by; Terra had the join, not the catalog/column entries.
  { key: 'contact_persona',          label: 'Persona',         group: 'Contact',   type: 'picklist' },
  { key: 'contact_stage',            label: 'Contact stage',   group: 'Contact',   type: 'picklist' },
  { key: 'account_sales_tier',       label: 'Tier',            group: 'Account',   type: 'picklist' },
  { key: 'account_billing_country',  label: 'Account country', group: 'Account',   type: 'text' },
  { key: 'account_linkedin',         label: 'Account LinkedIn', group: 'Account',   type: 'text', allowedOperators: ['is_empty', 'is_not_empty'] },
  { key: 'account_email_domain_link', label: 'Domain',         group: 'Account',   type: 'text' },
  { key: 'account_industry',         label: 'Industry',        group: 'Account',   type: 'picklist' },
  { key: 'account_fit',              label: 'Account fit',     group: 'Account',   type: 'picklist' },
  { key: 'account_product_tier',     label: 'Product tier',    group: 'Account',   type: 'picklist' },
  { key: 'flow_name',                label: 'Flow',       group: 'Gong Flow', type: 'text' },
  { key: 'flow_enrolled_at',         label: 'Enrolled in flow', group: 'Gong Flow', type: 'date' },
  { key: 'flow_last_modified_at',    label: 'Flow last modified', group: 'Gong Flow', type: 'date' },
  { key: 'gong_verified_at',         label: 'Last checked in Gong', group: 'Gong Flow', type: 'date' },
  // Signals V3 PR 1, U6 — `gong_engage_flow.last_step_completed_date`, a Postgres DATE (Salesforce
  // stores the DAY, not the time), so day-granular: reps get "since N days", never hours. Source for
  // PR 2's "Days on step".
  { key: 'step_date',                label: 'Last step completed', group: 'Gong Flow', type: 'date', granularity: 'day' },
  { key: 'signal_source',            label: 'Signal source',   group: 'Signal',    type: 'picklist' },
  // Gong reconcile U23 — the four-state rep value (`SIGNAL_LIST_FIELD_MAP.gong_state`,
  // derived live from `REP_STATE_RULES`). A filter/group field so presets (U24)
  // and saved views can reference it; `signal_status` stays beside it untouched.
  // Values are `REP_STATES` (validated by `validateViewFieldReferences`), labelled for reps.
  { key: 'gong_state',               label: 'Gong state',      group: 'Signal',    type: 'picklist', values: REP_STATES.map(value => ({ value, label: REP_STATE_LABELS[value] })) },
  { key: 'campaign_name',            label: 'Campaign',   group: 'Campaign',  type: 'text' },
  // Signals V3 PR 1, U4 — the ACCOUNT's owner / ClayDR / Growth Strategist.
  // `peopleScope` mirrors ACCOUNT_FILTER_FIELDS: an account is only ever owned
  // by a GTME/GS and its ClayDR is only ever a ClayDR; the GS keeps the full
  // directory, as it does there.
  { key: 'account_owner_name',       label: 'Account owner',      group: 'Account', type: 'person', peopleScope: 'account_owners' },
  { key: 'account_claydr_name',      label: 'Account ClayDR',     group: 'Account', type: 'person', peopleScope: 'account_claydrs' },
  { key: 'account_gs_name',          label: 'Growth Strategist',  group: 'Account', type: 'person' },
];

const PASSTHROUGH_FILTER_FIELDS: FilterFieldCatalogEntry[] = SIGNAL_PASSTHROUGH_FIELDS.map(f => ({
  key: f.key,
  label: f.label,
  group: f.entity === 'contact' ? 'Contact' : 'Account',
  type: f.type,
  ...(f.values ? { values: [...f.values] } : {}),
  ...(SIGNAL_PASSTHROUGH_DAY_KEYS.has(f.key) ? { granularity: 'day' as const } : {}),
}));

export const SIGNAL_DISPLAY_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  ...HAND_WRITTEN_DISPLAY_FILTER_FIELDS,
  ...PASSTHROUGH_FILTER_FIELDS,
];

// ── Row / response schema ───────────────────────────────────────────────────

/**
 * The Signals row IS the `terra_signals` view row plus display joins (D3/
 * D13 precedent from `TerraSignalRowSchema` itself) — not a new shape.
 * `flow_name`/the three Gong dates are join results (U3), nullable exactly
 * like every other display join in this codebase (a row with no matching
 * Gong flow still parses). Types are `z.infer` only
 * (`terra/no-parallel-schema-and-type`).
 */
function passthroughRowShape(): Record<string, z.ZodType> {
  const shape: Record<string, z.ZodType> = {};
  for (const f of SIGNAL_PASSTHROUGH_FIELDS) {
    // `.nullish()`: the route always projects every one of these (null when
    // the contact/account has no value), but they are optional DISPLAY data —
    // a consumer holding a row without them (a fixture, an older cached page)
    // must not fail the whole page's fail-closed parse over a column.
    shape[f.key] =
      f.type === 'number' ? z.number().nullish()
      : f.type === 'boolean' ? z.boolean().nullish()
      : f.type === 'date' ? IsoDateStringSchema.nullish()
      : z.string().nullish();
  }
  return shape;
}

/**
 * WIRE-COMPAT RULE (stale tabs). A tab still running an older bundle parses
 * this schema fail-closed, and `toStripView` (web/app-home/src/lib/stripView.ts)
 * only tolerates keys the server ADDED — never keys it REMOVED.
 *   - A new wire key must be `.nullish()`, so an older server's rows (during a
 *     rolling deploy) and older bundles both stay valid.
 *   - To remove a key, keep projecting it (as `null`) for one deploy, or accept
 *     that open stale tabs show an error until reload. Removing the four
 *     `*_meeting_link` keys outright (b8990cffa) is what broke stale Signals
 *     tabs on 2026-10-23.
 */
export const SignalRowSchema = TerraSignalRowSchema.extend({
  ...passthroughRowShape(),
  contact_title: z.string().nullable(),
  contact_mailing_city: z.string().nullable(),
  contact_mailing_state: z.string().nullable(),
  contact_mailing_country: z.string().nullable(),
  contact_linkedin: z.string().nullable(),
  // QA batch 3, D-E — the seven ordinary display columns.
  contact_persona: z.string().nullable(),
  contact_stage: z.string().nullable(),
  account_industry: z.string().nullable(),
  account_fit: z.string().nullable(),
  account_product_tier: z.string().nullable(),
  signal_source: z.string().nullable(),
  campaign_name: z.string().nullable(),
  // Signals V3 PR 1, U4 — `.nullish()` for the same reason as the generated passthrough fields above.
  account_owner_name: z.string().nullish(),
  // Owner's meeting link for the calendar menu. `.nullish()` per the WIRE-COMPAT RULE above: stale tabs' rows lack the key.
  account_owner_meeting_link: z.string().nullish(),
  account_claydr_name: z.string().nullish(),
  account_gs_name: z.string().nullish(),
  account_sales_tier: z.string().nullable(),
  account_billing_country: z.string().nullable(),
  account_linkedin: z.string().nullable(),
  account_email_domain_link: z.string().nullable(),
  flow_name: z.string().nullable(),
  flow_enrolled_at: IsoDateStringSchema.nullable(),
  flow_last_modified_at: IsoDateStringSchema.nullable(),
  gong_verified_at: IsoDateStringSchema.nullable(),
  // U6 — the flow's last-step-completed DAY; `.nullish()` like the other additive display columns.
  step_date: IsoDateStringSchema.nullish(),
  // The probe's own verdict, from the SAME `gong_flow_probe` row as
  // `gong_verified_at` (fix-plan-batch2.md D-A) — wire-only: not in
  // `SIGNAL_FIELDS`/`SIGNAL_DISPLAY_FILTER_FIELDS`; read by
  // `resolveSignalVerificationState`, never filtered on.
  gong_probe_status: GongProbeStatusSchema,
  // Wire-only (Signals V3 PR 2, U2) — not in SIGNAL_FIELDS/SIGNAL_DISPLAY_FILTER_FIELDS.
  // Wire value is a calendar date `YYYY-MM-DD` (a Postgres DATE): the client must
  // never `new Date(str)` it in UTC — parse it as a local calendar date.
  step_started_at: IsoDateStringSchema.nullable(),
  // Signals V3 PR 2 — these two arrive via PR 1's generated passthrough (SQL, filter, label); they are
  // re-declared only so the PR 2 cells/actions read them typed (the generated shape is Record<string, ZodType>).
  contact_best_phone: z.string().nullish(),
  // Same passthrough, re-declared typed: the Signals bulk dial leaves do-not-call contacts out (`partitionDialableSignals`).
  contact_do_not_call: z.boolean().nullish(),
  account_type: z.string().nullish(),
  // Signals V3 PR 3, U14a — wire-only contact names for the routing-link URL.
  // `contact_email` already arrives via the generated passthrough; re-declared only so the
  // booking-links control reads it typed (same reason as `contact_best_phone` above).
  contact_email: z.string().nullish(),
  contact_first_name: z.string().nullish(),
  contact_last_name: z.string().nullish(),
  // Gong reconcile U23 — the four-state rep value (`REP_STATES`, derived live in
  // SQL from `REP_STATE_RULES`) and the Gong enrollment owner (wire-only).
  // `.nullish()` per the WIRE-COMPAT RULE above.
  gong_state: z.enum(REP_STATES).nullish(),
  gong_owner_email: z.string().nullish(),
});
export type SignalRow = z.infer<typeof SignalRowSchema>;

/**
 * R5 (Signals tab PR 1, review round 1, D-E) — named `FlowSignals*`, not
 * `Signals*`: `src/shared/schemas/sfdc-signals.ts` already declares its own
 * (unrelated, E&S) `SignalsResponseSchema`, and `terra/no-duplicate-schema-name`
 * requires every `*Schema` identifier to be declared exactly once repo-wide
 * (`docs/v4-system/schemas-as-source-of-truth.md`). `Flow` is the same
 * prefix the list route already chose for `FlowSignalsQuerySchema`, for
 * exactly this reason — the E&S catalog is the incumbent and keeps its name.
 */
export const FlowSignalsResponseSchema = z.object({
  rows: z.array(SignalRowSchema),
  next_cursor: z.string().nullable(),
});
export type FlowSignalsResponse = z.infer<typeof FlowSignalsResponseSchema>;

// ── Presets ──────────────────────────────────────────────────────────────────

/**
 * R2 — every Signals preset scopes to the Signal Action arm only (the other
 * `terra_signals` arms — Campaign, Terra queue — are Plays/Tracker
 * territory, not Signals). Carried on every preset below, always first in
 * its filter group.
 */
export const SIGNAL_SOURCE_CONDITION: FilterCondition = {
  id: 'x-signal-source', field: 'source_system', operator: 'equals', value: 'Salesforce Signal Action',
};

const ASSIGNEE_IS_ME: FilterCondition = { id: 'c-assignee-me', field: 'assignee', operator: 'equals', value: ME_TOKEN };
const ACTION_STATE_TODO: FilterCondition = { id: 'c-todo', field: 'action_state', operator: 'equals', value: 'To do' };
/** Gong reconcile U24 — the rep-facing "Do it now" state (`gong_state`, derived
 *  live from the Gong probe by `REP_STATE_RULES`). It already encodes
 *  manual (not an auto step) + due + not paused/ended, so it REPLACES the two
 *  legacy default-view conditions (`signal_tactic not_contains auto`,
 *  `signal_status not_equals Paused`). Those FIELDS stay in the catalog so saved
 *  user views that reference them keep resolving; only the presets changed. */
export const GONG_STATE_DO_IT_NOW: FilterCondition = { id: 'c-gong-do-it-now', field: 'gong_state', operator: 'equals', value: 'do_it_now' };
const GONG_STATE_WAITING: FilterCondition = { id: 'c-gong-waiting', field: 'gong_state', operator: 'equals', value: 'waiting' };
const GONG_STATE_FIX_IN_GONG: FilterCondition = { id: 'c-gong-fix-in-gong', field: 'gong_state', operator: 'equals', value: 'fix_in_gong' };

const MY_SIGNALS_COLUMNS = pinContactFirst([
  'contact_linkedin', 'contact_title', 'contact_location', 'local_time',
  'account_name', 'account_email_domain_link', 'account_sales_tier', 'flow_name',
  'signal_tactic', 'step_number', 'due_date', 'signal_status', 'event_date',
]);

function pinContactFirst(cols: string[]): string[] {
  return ['contact_name', ...cols.filter(c => c !== 'contact_name')];
}

function preset(p: Omit<FlowView, 'createdBy' | 'entity'>): FlowView {
  return { createdBy: null, entity: 'signal', ...p };
}

export const SIGNAL_PRESETS: FlowView[] = [
  preset({
    id: 'signal-my',
    name: 'My Signals',
    filter: [{ id: 'g1', logic: 'AND', conditions: [SIGNAL_SOURCE_CONDITION, ASSIGNEE_IS_ME, ACTION_STATE_TODO, GONG_STATE_DO_IT_NOW] }],
    sort: { field: 'priority', dir: 'ASC' },
    groupBy: [],
    columns: MY_SIGNALS_COLUMNS,
    isDefault: true,
  }),
  preset({
    id: 'signal-all',
    name: 'All Signals',
    filter: [{ id: 'g1', logic: 'AND', conditions: [SIGNAL_SOURCE_CONDITION, ACTION_STATE_TODO, GONG_STATE_DO_IT_NOW] }],
    sort: { field: 'priority', dir: 'ASC' },
    groupBy: [],
    columns: pinContactFirst([
      'assignee', 'contact_linkedin', 'contact_title', 'contact_location', 'local_time',
      'account_name', 'account_email_domain_link', 'account_sales_tier', 'flow_name',
      'signal_tactic', 'due_date', 'signal_status', 'event_date',
    ]),
    isDefault: false,
  }),
  // Ordering is visible in the view switcher: the two Gong views sit right after
  // All Signals, ahead of Snoozed / Closed / Overdue.
  preset({
    id: 'signal-upcoming',
    name: 'Upcoming',
    filter: [{ id: 'g1', logic: 'AND', conditions: [SIGNAL_SOURCE_CONDITION, ASSIGNEE_IS_ME, ACTION_STATE_TODO, GONG_STATE_WAITING] }],
    sort: { field: 'priority', dir: 'ASC' },
    groupBy: [],
    columns: MY_SIGNALS_COLUMNS,
    isDefault: false,
  }),
  preset({
    id: 'signal-fix-in-gong',
    name: 'Fix in Gong',
    filter: [{ id: 'g1', logic: 'AND', conditions: [SIGNAL_SOURCE_CONDITION, ASSIGNEE_IS_ME, ACTION_STATE_TODO, GONG_STATE_FIX_IN_GONG] }],
    sort: { field: 'priority', dir: 'ASC' },
    groupBy: [],
    columns: [...MY_SIGNALS_COLUMNS, 'gong_owner_email'],
    isDefault: false,
  }),
  preset({
    id: 'signal-snoozed',
    name: 'Snoozed',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [SIGNAL_SOURCE_CONDITION, ASSIGNEE_IS_ME, { id: 'c-snoozed', field: 'action_state', operator: 'equals', value: 'Snoozed' }],
    }],
    sort: { field: 'snooze_until', dir: 'ASC' },
    groupBy: [],
    columns: pinContactFirst([
      'contact_linkedin', 'contact_title', 'contact_location', 'local_time',
      'account_name', 'account_email_domain_link', 'flow_name', 'signal_tactic',
      'snooze_until', 'due_date', 'event_date',
    ]),
    isDefault: false,
  }),
  preset({
    id: 'signal-closed',
    name: 'Closed',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [
        SIGNAL_SOURCE_CONDITION, ASSIGNEE_IS_ME,
        { id: 'c-closed-states', field: 'action_state', operator: 'in_any_of', value: ['Completed', 'Archived'] },
      ],
    }],
    sort: { field: 'action_completed_at', dir: 'DESC' },
    groupBy: [],
    columns: pinContactFirst([
      'contact_linkedin', 'contact_title', 'contact_location', 'local_time',
      'account_name', 'account_email_domain_link', 'flow_name', 'action_reason',
      'action_completed_at', 'action_completed_by_name', 'event_date',
    ]),
    isDefault: false,
  }),
  preset({
    id: 'signal-overdue',
    name: 'Overdue',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [
        SIGNAL_SOURCE_CONDITION, ASSIGNEE_IS_ME, ACTION_STATE_TODO,
        // OQ1 (resolved, architect option c) — `before_today` is a
        // code-only date operator (filter-operators.ts's
        // `NON_UI_OPERATORS_BY_TYPE.date`): valid at write time, no
        // FilterPopover control or `condition-to-sql.ts` SQL case yet.
        // Those two land with the list-route/client-wiring beads that
        // actually need this preset to QUERY (2sx.10 / 2sx.7) — see this
        // bead's Discoveries.
        { id: 'c-overdue', field: 'due_date', operator: 'before_today', value: null },
        // `do_it_now` = manual and due (D9), the same intent the old auto/Paused
        // pair expressed; `before_today` still narrows it to overdue.
        GONG_STATE_DO_IT_NOW,
      ],
    }],
    sort: { field: 'due_date', dir: 'ASC' },
    groupBy: [],
    columns: pinContactFirst([
      'contact_linkedin', 'contact_title', 'contact_location', 'local_time',
      'account_name', 'account_email_domain_link', 'flow_name', 'signal_tactic',
      'due_date', 'days_open', 'signal_status',
    ]),
    isDefault: false,
  }),
];

export const SIGNAL_LANDING_PRESET_ID = 'signal-my';
